import { CPlan } from '../mdl/c-plan';
import { CProducer } from '../mdl/c-producer';
import { CLoginUser, cUserAnalyticsProfile } from '../mdl/c-user';
import { CVenueSceneView } from '../mdl/c-venue';
import { LIdentityProvider } from '../mdl/l-identity';
import { LScope } from '../mdl/l-scope';
import { PPromo } from '../mdl/p-promo';
import { $recaptcha } from '../ui/media/recaptcha';
import { $analytics } from '../y/analytics';
import { $api } from '../y/api';
import { setXavisExperiments, yAddCrashCallback } from '../y/debug';
import { $fn } from '../y/fn';
import { $http } from '../y/http';
import { the_language, ySetLanguage } from '../y/i18n';
import { $storage } from '../y/storage';
import { YCountryId, YId } from '../y/types';
import { meTriggerLogin, meTriggerLogout } from './login-event';

export let the_user: CLoginUser | undefined;
export let the_alt_token: string | undefined;
export let the_login_scope: LScope | undefined;
export let the_login_token: string | undefined;
export let the_reset_token: string | undefined;
export let the_settings: TLLoginResponseSettings | undefined;
export let the_user_is_new: boolean | undefined;
export let the_intercom_user_hash: string | undefined;

interface TLStats {
    active_sessions_city: { [city: YId]: number };
    active_sessions: number;
    all_sessions: number;
    active_events: number;
    all_events: number;
    confirmed_bookings: number;
    cities: number;
    venues: number;
}

declare global {
    interface Window {
        y_default_country: YCountryId;
        ab_plans: CPlan[];
        ab_stats: TLStats;
    }
}

export interface MEIdentityInfo {
    exists: boolean;
    avatar: string;
    first_name: string;
    last_name: string;
    email: string;
    provider: LIdentityProvider;
    token: string;
}

interface TLLoginResponseSettings {
    loyalty_months: number;
    loyalty_days: number;
}

export interface MELoginEnvironment {
    codes?: string[];
    role_invite?: string;
    verified_phone?: string;
    phone_token?: string;
}

interface TLPreSignupResponse {
    phone_exists: boolean;
    verified_phone_exists: boolean;
    email_exists: boolean;
    test_mode: boolean;
}

export interface MELoginResponse {
    token: string;
    alt_token: string;
    scope: LScope;
    is_signup: boolean;
    intercom_user_hash?: string;
    user: CLoginUser;
    settings: TLLoginResponseSettings;
    venues?: CVenueSceneView[];
    producers?: CProducer[];
    keywords?: string[];
    promos?: PPromo[];
    identity?: undefined;
}

interface MELoginIdentityResponse {
    scope?: undefined;
    identity: MEIdentityInfo;
}

type MEMaybeLoginResponse = MELoginResponse | MELoginIdentityResponse;

function parseLoginResponse(data: MELoginResponse, auto?: boolean) {
    the_login_scope = data.scope;
    the_login_token = data.token;
    the_user = data.user;
    the_alt_token = data.alt_token;
    the_settings = data.settings;
    the_user_is_new = data.is_signup;
    the_intercom_user_hash = data.intercom_user_hash;
    $http.addGlobalHeader('X-Abundo-Token', the_login_token);

    if(the_user.email === 'gratal@gmail.com') {
        setXavisExperiments(true);
        localStorage.setItem('xavis_experiments', 'true');
    }

    $storage.putString('abundo_access_token_' + the_login_scope, the_login_token, !auto);

    meTriggerLogin(data);

    $analytics.identify(the_user.analytics_id, cUserAnalyticsProfile(the_user), undefined, undefined, undefined, data.is_signup, the_intercom_user_hash);
    ySetLanguage(the_user.language);
}

export function mePreSignup(scope: LScope, email: string, phone: string, identity_provider: string, identity_token: string) {
    return $api.POST<TLPreSignupResponse>('me/pre_signup', { scope, email, phone, identity_provider, identity_token }).catch($api.errorHandler);
}

export function meSignup(data: any, scope: LScope, environment: MELoginEnvironment) {
    data.scope = scope;
    data.environment = environment;
    data.language = the_language;
    if(!data.country)
        data.country = window.y_default_country;

    return $recaptcha.execute('signup').then(token => {
        data.recaptcha = token;
        return $api.POST<MELoginResponse>('me/signup', data).then(parseLoginResponse);
    });
}

interface MELoginRequest {
    scope: LScope;

    token?: string;
    as_user?: string;

    phone?: string;
    email?: string;
    password?: string;

    recaptcha?: string;

    identity_provider?: LIdentityProvider;
    identity_token?: string;

    environment: MELoginEnvironment;
}

function doLogin(params: MELoginRequest, auto: boolean) {
    return $api.POST<MEMaybeLoginResponse>('me/login', params).then(response => {
        if(response.scope)
            parseLoginResponse(response, auto);
        return response;
    });
}

export function meAutoLogin(scope: LScope, environment: MELoginEnvironment, as_user?: string, reset?: string) {
    if(reset) {
        the_reset_token = reset;
        return Promise.resolve(false);
    }

    const params: MELoginRequest = { scope: scope, environment: environment }

    if(as_user) {
        params.as_user = as_user;
    } else {
        params.token = $storage.getString('abundo_access_token_' + scope);
        if(!params.token)
            return Promise.resolve(false);
    }

    return doLogin(params, true).then($fn.TRUE, () => {
        $storage.remove('abundo_access_token_' + scope);
        return false;
    });
}

export function meEmailLogin(email: string, password: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ email, password, scope, environment }, false);
}

export function meIdentityLogin(provider: LIdentityProvider, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ identity_provider: provider, identity_token: token, scope, environment }, false).then(response => response.identity);
}

export function meEmailTokenLogin(email: string, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ email, identity_provider: 'email', identity_token: token, scope, environment }, false);
}

export function mePhoneTokenLogin(phone: string, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ phone, identity_provider: 'phone', identity_token: token, scope, environment }, false);
}

export function meLogout() {
    if(the_login_scope) {
        $analytics.track('User Logout');
        $storage.remove('abundo_access_token_' + the_login_scope);
        meTriggerLogout();
        $api.POST('me/logout');
        $http.removeGlobalHeader('X-Abundo-Token');
        the_user = undefined;
        the_login_scope = undefined;
        the_login_token = undefined;
        the_alt_token = undefined;
        the_intercom_user_hash = undefined;
    }
}

export function meForgotPassword(scope: LScope, email: string) {
    return $api.POST('me/forgot_password', { scope: scope, email: email }).catch($api.errorHandler);
}

export function meResetPassword(_scope: LScope, password: string) {
    return $api.POST<MELoginResponse>('me/reset_password', { password: password, token: the_reset_token }).then(response => {
        the_reset_token = undefined;
        return parseLoginResponse(response, false);
    });
}

yAddCrashCallback((info) => {
    $http.POST('/api/v1/web_log', info);
});
